import React, { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  campaignNames,
  DOWNLOAD_DRIVER_CLICK_LINK,
  DOWNLOAD_DRIVER_IMPRESSION,
} from '../../../core/analytics/events';
import { getAccountSummaryById } from '../../../core/dapi/account';
import { isIOS, isIosSafariOnly } from '../../../core/util/device';
import Avatar from '../../Account/Profile/Avatar';
import { shouldShowAppInstallPrompt } from '../../AddToHomeScreen/util';
import { PWA_ADD_TO_HOME_SCREEN } from '../../App/util/tracking';
import { getNativeStoreDownloadLink } from '../../DownloadDriver/util';
import AddProfileModal from './AddProfileModal';
import ProfileList from './ProfileList';
import styled from 'styled-components';
import { colors } from '../../../constants/colors';
import { fontSize, fontFamily } from '../../../constants/text';
import { analyticsTrackEvent } from '../../../core/analytics';
import { Actions, Events } from '../../Account/util/analytics';
import { receiveAccountSummary, receiveUserProfileForAccount } from '../../../actions/account';
import { apiPatchDispatchableWithoutSource, apiGetDispatchable } from '../../../core/dapi';
import { getUserProfileById } from '../../../core/dapi/userProfile';
import { noOp } from '../../../actions';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Drawer from '@material-ui/core/Drawer';
import PersonRemoveIcon from '../../SolvPatternLibrary/Icons/PersonRemove';
import PersonAddIcon from '../../SolvPatternLibrary/Icons/PersonAdd';
import AboutUsIcon from '../../SolvPatternLibrary/Icons/AboutUs';
import LogOutIcon from '../../SolvPatternLibrary/Icons/LogOut';
import SettingsIcon from '@material-ui/icons/Settings';
import AndroidIcon from '@material-ui/icons/Android';
import PhoneIphone from '@material-ui/icons/PhoneIphone';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import DrawerAction from './DrawerAction';
import history from '../../../core/history';
import { createUserProfileUrl } from '../../../core/dapi/userProfile';
import { setAjaxPending, setAjaxError, showModal } from '../../../actions/runtime';
import { apiPostDispatchable } from '../../../core/dapi';
import AboutUsModal from './AboutUsModal';

const CloseChevron = styled(ChevronRightIcon)`
  fill: ${colors.squid};
  font-size: ${fontSize.quiteExtraExtraLarge};
`;

const DrawerTop = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 24px 48px 18px 24px;
`;

const Title = styled.div`
  font-size: ${fontSize.large};
  font-family: ${fontFamily.bold};
  flex-grow: 1;
  color: ${colors.squid};
  padding-left: 18px;
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FirstName = styled.div`
  color: ${colors.squid};
  font-size: ${fontSize.small};
`;

const ExpandIcon = styled(ExpandMore)`
  color: ${colors.squid};
  margin-right: 4px;
`;

const ActionList = styled(List)`
  margin: 24px 0;
  padding: 0;
  background: ${colors.theWhiteAlbum};
  border-top: 1px solid ${colors.borderLine};
  border-bottom: 1px solid ${colors.borderLine};
`;

const ActionDivider = styled(Divider)`
  color: ${colors.justAnotherGray};
  margin: 0 0 0 72px;
`;

const StyledMenuIcon = styled.div`
  cursor: pointer;
`;

const StyledDrawer = styled(Drawer)`
  & .drawer-paper {
    width: 90vw;
    max-width: 400px;
    background-color: ${colors.greyDim};
  }
`;

const Container = styled.div`
  background-color: ${colors.greyDim};
`;

class LoggedInMenu extends PureComponent {
  static propTypes = {
    accountSummary: PropTypes.object,
    createUserProfile: PropTypes.func,
    currentProfile: PropTypes.object,
    deleteUserProfile: PropTypes.func,
    handleLogout: PropTypes.func,
    profileColor: PropTypes.object,
    submitting: PropTypes.bool,
    openModal: PropTypes.func,
  };

  static defaultProps = {
    accountSummary: { user_profiles: [] },
    currentProfile: {},
  };

  state = {
    open: false,
    edit: false,
    edited: false,
    add: false,
    aboutUsModalOpen: false,
  };

  onClickProfileSelector = () => {
    const { accountSummary } = this.props;
    const numberOfProfiles = accountSummary.user_profiles && accountSummary.user_profiles.length;
    analyticsTrackEvent(Events.ACCOUNT_NAVIGATION, {
      action: Actions.TOUCH_PRIMARY_TAB,
      label: 'Profile Selector',
      numberOfProfiles,
    });
    this.handleOpenDrawer();
  };

  getDrawerOpener = () => {
    const { currentProfile, profileColor } = this.props;

    return (
      <IconContainer>
        <FirstName>{currentProfile.first_name}</FirstName>
        <ExpandIcon fontSize="small" />
        <Avatar profileColor={profileColor.primary} userProfile={currentProfile} />
      </IconContainer>
    );
  };

  getContent = () => {
    const { accountSummary, currentProfile, handleLogout } = this.props;

    return (
      <div data-testid="LoggedInMenu">
        <DrawerTop onClick={this.handleCloseDrawer}>
          <CloseChevron color="primary" fontSize="large" />
          <Title>{this.state.edit ? 'Remove Profile' : 'Account'}</Title>
        </DrawerTop>
        <ProfileList
          accountSummary={accountSummary}
          currentProfile={currentProfile}
          deleteUserProfile={this.handleDeleteProfile}
          isInEditMode={this.state.edit}
          onSelect={this.handleCloseDrawer}
        />

        {!this.state.edit && (
          <ActionList>
            <DrawerAction
              id="Add Family"
              onClick={this.handleAddFamily}
              icon={<PersonAddIcon color="primary" />}
              actionText="Add Family"
            />
            <ActionDivider />
            <DrawerAction
              id="Remove Profile"
              onClick={this.handleRemoveProfileClick}
              icon={<PersonRemoveIcon color="primary" />}
              actionText="Remove Profile"
            />
            <ActionDivider />
            <DrawerAction
              id="Account Settings"
              onClick={this.goToAccountSettings}
              icon={<SettingsIcon color="primary" />}
              actionText="Account Settings"
            />
            <ActionDivider />
            <DrawerAction
              id="About"
              onClick={this.handleAboutUsClick}
              icon={<AboutUsIcon />}
              actionText="About Us"
            />
            {(shouldShowAppInstallPrompt() || isIosSafariOnly()) && (
              <Fragment>
                <ActionDivider />
                <DrawerAction
                  id="Download App"
                  onClick={this.handleDownloadApp}
                  icon={isIOS() ? <PhoneIphone color="primary" /> : <AndroidIcon color="primary" />}
                  actionText="Download App"
                />
              </Fragment>
            )}
          </ActionList>
        )}

        {!this.state.edit && (
          <ActionList>
            <DrawerAction
              id="Sign out"
              onClick={handleLogout}
              icon={<LogOutIcon />}
              actionText="Log Out"
              showArrow={false}
            />
          </ActionList>
        )}

        {this.state.edit && (
          <ActionList>
            <DrawerAction
              id="Cancel"
              onClick={this.handleRemoveProfileClick}
              icon={null}
              actionText={this.state.edited ? 'Done' : 'Cancel'}
              showArrow={false}
              textLink
            />
          </ActionList>
        )}

        <AddProfileModal
          accountSummary={this.props.accountSummary}
          closeModal={this.handleCloseAddFamily}
          createUserProfile={this.props.createUserProfile}
          open={this.state.add}
          openModal={this.props.openModal}
          submitting={this.props.submitting}
        />

        <AboutUsModal closeModal={this.handleCloseAboutUs} open={this.state.aboutUsModalOpen} />
      </div>
    );
  };

  handleAboutUsClick = () => {
    analyticsTrackEvent(Events.ACCOUNT_NAVIGATION, {
      action: Actions.PROFILE_SELECTOR,
      label: 'Touch About Us',
    });
    this.setState({ aboutUsModalOpen: true });
  };

  handleCloseAboutUs = () => this.setState({ aboutUsModalOpen: false });

  handleCloseDrawer = () => {
    analyticsTrackEvent(Events.CLOSE_DRAWER);
    this.setState({ open: false });
  };

  handleDeleteProfile = userProfileId => {
    this.props.deleteUserProfile(userProfileId);
    this.setState({ edited: true });
  };

  handleOpenDrawer = () => {
    analyticsTrackEvent(Events.OPEN_DRAWER);
    this.setState({ open: true });

    if (isIosSafariOnly()) {
      analyticsTrackEvent(DOWNLOAD_DRIVER_IMPRESSION, {
        campaignName: campaignNames.ACCOUNT_DRAWER,
      });
    }
  };

  handleDownloadApp = () => {
    if (isIOS()) {
      const campaignName = campaignNames.ACCOUNT_DRAWER;
      analyticsTrackEvent(DOWNLOAD_DRIVER_CLICK_LINK, { campaignName });
      window.location.href = getNativeStoreDownloadLink(campaignName);
      return;
    }

    this.handleCloseDrawer();
    window.deferredA2HSPrompt && window.deferredA2HSPrompt.prompt();
    analyticsTrackEvent(PWA_ADD_TO_HOME_SCREEN, {
      action: 'solv prompt',
      origin: 'account drawer',
    });
  };

  goToAccountSettings = () => {
    this.handleCloseDrawer();
    analyticsTrackEvent(Events.ACCOUNT_NAVIGATION, {
      action: Actions.PROFILE_SELECTOR,
      label: 'Touch Settings',
    });
    history.push('/account/settings');
  };

  handleAddFamily = e => {
    analyticsTrackEvent(Events.ACCOUNT_NAVIGATION, {
      action: Actions.PROFILE_SELECTOR,
      label: 'Touch Add Family',
    });
    this.setState({ add: true });
  };

  handleCloseAddFamily = e => {
    analyticsTrackEvent(Events.ACCOUNT_NAVIGATION, {
      action: Actions.PROFILE_SELECTOR,
      label: 'Close Add Family',
    });
    this.setState({ add: false });
  };

  handleRemoveProfileClick = e => {
    this.setState({ edit: !this.state.edit, edited: false });
  };

  render = () => (
    <div>
      <StyledMenuIcon onClick={this.onClickProfileSelector}>
        {this.getDrawerOpener()}
      </StyledMenuIcon>
      <StyledDrawer
        anchor="right"
        open={this.state.open}
        onClose={this.handleCloseDrawer}
        classes={{ paper: 'drawer-paper' }}
      >
        <Container tabIndex={0} role="button">
          {this.getContent()}
        </Container>
      </StyledDrawer>
    </div>
  );
}

const mapStateToProps = state => ({
  submitError: state.runtime.ajaxError,
  submitting: state.runtime.ajaxPending,
});

const mapDispatchToProps = dispatch => ({
  createUserProfile: userProfileData => {
    dispatch(setAjaxPending(true));

    const onSuccess = response => {
      dispatch(setAjaxPending(false));
      return receiveUserProfileForAccount(response);
    };

    dispatch(apiPostDispatchable(createUserProfileUrl(), userProfileData, onSuccess, setAjaxError));
  },

  deleteUserProfile: userProfileId => {
    const onSuccess = response => {
      const url = getAccountSummaryById(response.account_id);
      dispatch(apiGetDispatchable(url, receiveAccountSummary, noOp));
      return receiveUserProfileForAccount(response);
    };

    dispatch(
      apiPatchDispatchableWithoutSource(
        getUserProfileById(userProfileId),
        { is_hidden: true },
        onSuccess,
        noOp
      )
    );
  },

  openModal: modalId => dispatch(showModal(modalId)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LoggedInMenu);
